// /DeviceConfigs/DeviceConfigEditFormInner.js
import React, { useEffect } from 'react';
import { Modal, Card, Typography, Form } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import DeviceConfigTypeSelector, { DEVICE_TYPE_META } from './components/DeviceConfigTypeSelector';
import { renderAllTabs, getTabListByType } from './helpers/DeviceConfigFormHelper';

import TabEvent from './DeviceConfigFormModule/TabEvent';
import TabSelfCheckin from './DeviceConfigFormModule/TabSelfCheckin';
import TabImages from './DeviceConfigFormModule/TabImages';
import TabEntranceControl from './DeviceConfigFormModule/TabEntranceControl';
import TabCloakroom from './DeviceConfigFormModule/TabCloakroom';
import TabNfcLink from './DeviceConfigFormModule/TabNfcLink';
import TabTexts from './DeviceConfigFormModule/TabTexts';
import TabParticipantTypes from './DeviceConfigFormModule/TabParticipantTypes';
import TabCustomFields from './DeviceConfigFormModule/TabCustomFields';
import TabServer from './DeviceConfigFormModule/TabServer';
import TabLeadCapture from './DeviceConfigFormModule/TabLeadCapture';
import TabAnimations from './DeviceConfigFormModule/TabAnimations';

const { Title } = Typography;

// Dumb/presentational component. No data fetching, no axios.
const DeviceConfigEditFormInner = ({
                                       form,
                                       open,
                                       initialFormValues,
                                       activeTabKey,
                                       setActiveTabKey,
                                       selectedEventId,
                                       setSelectedEventId,
                                       showEFN,
                                       isProgrammePointDisabled,
                                       isLocationDisabled,
                                       events,
                                       programmePoints,
                                       locations,
                                       participantTypes,
                                       customFields,
                                       handleEventChange,
                                       handleSave,
                                       handleClose,
                                       admin,
                                   }) => {
    const deviceConfigName =
        Form.useWatch('DeviceConfigName', form) ?? initialFormValues?.DeviceConfigName ?? 'Unnamed';
    const currentType = Form.useWatch('Type', form);
    const currentMode = Form.useWatch('Mode', form) ?? initialFormValues?.Mode ?? 'entranceControl';
    const [themeSwitchChecked, setThemeSwitchChecked] = React.useState(
        initialFormValues?.theme ?? false
    );

    // fallback if they are not yet defined
    const safeDeviceConfigName = deviceConfigName || 'Unnamed';
    const safeCurrentType = currentType || 'mobile';

    useEffect(() => {
        if (open && initialFormValues && form) {
            form.setFieldsValue(initialFormValues);
        }
    }, [open, initialFormValues, form]);

    // sync tab guardrails when type changes
    useEffect(() => {
        const invalidForMobile = ['images', 'participantTypes', 'selfcheckin'];
        const invalidForDesktop = ['entranceControl', 'customfields'];
        const invalidForLead = ['images', 'texts', 'participantTypes', 'selfcheckin', 'entranceControl'];

        const invalidMap = {
            mobile: invalidForMobile,
            desktop: invalidForDesktop,
            leadcapture: invalidForLead,
        };

        if (invalidMap[currentType]?.includes(activeTabKey)) {
            setActiveTabKey('event');
        }
    }, [currentType, activeTabKey, setActiveTabKey]);

    const modeMeta = DEVICE_TYPE_META[currentType] || { label: 'Unknown', color: '#999' };
    const tabList = getTabListByType(currentType, admin, currentMode);
    const currentSelected = Form.useWatch('CustomSeatFieldIds', form) || [];

    const tabsContent = {
        event: (
            <TabEvent
                form={form}
                selectedEventId={selectedEventId}
                setSelectedEventId={setSelectedEventId}
                handleEventChange={handleEventChange}
                events={events}
                programmePoints={programmePoints}
                locations={locations}
                isProgrammePointDisabled={isProgrammePointDisabled}
                isLocationDisabled={isLocationDisabled}
                themeSwitchChecked={themeSwitchChecked}
                setThemeSwitchChecked={setThemeSwitchChecked}
            />
        ),
        //entranceControl: <TabEntranceControl showEFN={showEFN} />,
        entranceControl:
            safeCurrentType === 'mobile' && currentMode === 'cloakroom'
                ? <TabCloakroom form={form} deviceConfigId={initialFormValues?._id} />
                : safeCurrentType === 'mobile' && currentMode === 'nfcLink'
                    ? <TabNfcLink form={form} deviceConfigId={initialFormValues?._id} />
                    : <TabEntranceControl showEFN={showEFN} />,
        customfields: (
            <TabCustomFields
                customFields={customFields}
                currentSelected={currentSelected}
                form={form}
            />
        ),
        selfcheckin: <TabSelfCheckin showEFN={showEFN} />,
        images: <TabImages form={form} />,
        texts: <TabTexts showEFN={showEFN} />,
        animations: <TabAnimations />,
        participantTypes: <TabParticipantTypes participantTypes={participantTypes} />,
        leadcapture: <TabLeadCapture />,
        server: <TabServer />,
    };

    return (
        <Modal
            open={open}
            onOk={handleSave}
            onCancel={handleClose}
            okText="Save"
            cancelText="Cancel"
            width="80%"
            style={{ top: 20 }}
            destroyOnHidden={true}
            title={
                <div style={{
                    fontSize: '24px',
                    color: '#1890ff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginLeft: '24px'
                }}>
                    {initialFormValues && initialFormValues._id ? <EditOutlined /> : <PlusOutlined />}
                    <strong>{safeDeviceConfigName}</strong>
                    &nbsp;–&nbsp;
                    <span style={{ color: modeMeta.color }}>{DEVICE_TYPE_META[safeCurrentType]?.label}</span>
                </div>
            }
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={initialFormValues}
            >
                <div style={{ padding: '24px' }}>
                    <Card
                        style={{ width: '100%' }}
                        title={<Title level={2} style={{ color: 'var(--ant-primary-color)' }}>Device Configuration</Title>}
                        extra={
                            <Form.Item name="Type" style={{ margin: 0 }}>
                                <DeviceConfigTypeSelector
                                    value={currentType}
                                    onChange={(newType) => {
                                        if (form?.setFieldsValue) {
                                            form.setFieldsValue({ Type: newType });
                                        }
                                        setActiveTabKey('event'); // ALWAYS return to TabEvent
                                    }}
                                />
                            </Form.Item>
                        }
                        tabList={tabList}
                        activeTabKey={activeTabKey}
                        onTabChange={setActiveTabKey}
                    >
                        {renderAllTabs(tabsContent, activeTabKey, admin)}
                    </Card>
                </div>
            </Form>
        </Modal>
    );
};

export default DeviceConfigEditFormInner;